import React, { createContext, useContext, useState, useEffect } from 'react'
import { useUser } from './UserContext'
import { apiClient } from '@/lib/api'

interface CandidateSkill {
  uuid: string
  skill_name: string
  skill_category: string
  proficiency_level: string
  years_of_experience: number
  is_primary_skill: boolean
  certification_related: boolean
}

interface SkillMatchResult {
  percentage: number
  requiredMatched: number
  requiredMissing: number  
  goodToHaveMatched: number
  totalRequired: number
  totalGoodToHave: number
}

interface CandidateSkillsState {
  skills: CandidateSkill[]
  isLoading: boolean
  lastUpdated: Date | null
  error: string | null
}

interface CandidateSkillsContextType {
  state: CandidateSkillsState
  calculateSkillMatch: (positionSkills: any[], positionCertifications?: any[]) => SkillMatchResult
  refreshSkills: () => Promise<void>
  clearSkills: () => void
}

const CandidateSkillsContext = createContext<CandidateSkillsContextType | undefined>(undefined)

export function CandidateSkillsProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CandidateSkillsState>({
    skills: [],
    isLoading: false,
    lastUpdated: null,
    error: null
  })
  
  const { state: userState } = useUser()

  const fetchCandidateSkills = async () => {
    if (!userState.isAuthenticated || !userState.profile?.is_jobseeker) {
      return
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }))
    
    try {
      const response = await apiClient.request('candidates/me/skills')
      
      if (response.success) {
        setState(prev => ({
          ...prev,
          skills: (response.data as any)?.skills || [],
          isLoading: false,
          lastUpdated: new Date(),
          error: null
        }))
      } else {
        throw new Error(response.error || 'Failed to fetch candidate skills')
      }
    } catch (error) {
      console.error('Error fetching candidate skills:', error)
      setState(prev => ({
        ...prev,
        skills: [],
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch skills'
      }))
    }
  }

  const refreshSkills = async () => {
    await fetchCandidateSkills()
  }

  const clearSkills = () => {
    setState({
      skills: [],
      isLoading: false,
      lastUpdated: null,
      error: null
    })
  }

  const normalizeSkillName = (skillName: string): string => {
    return skillName?.toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ') || ''
  }

  const skillsMatch = (candidateSkillName: string, positionSkillName: string): boolean => {
    const candSkillName = normalizeSkillName(candidateSkillName)
    const posSkillName = normalizeSkillName(positionSkillName)
    
    if (!candSkillName || !posSkillName) return false
    
    // Exact match
    if (candSkillName === posSkillName) return true
    
    // Partial match for compound skills
    if (candSkillName.includes(posSkillName) || posSkillName.includes(candSkillName)) {
      return true
    }
    
    // Handle common variations
    const variations: Record<string, string[]> = {
      'javascript': ['js', 'ecmascript'],
      'typescript': ['ts'],
      'react': ['reactjs', 'react.js'],
      'vue': ['vuejs', 'vue.js'],
      'angular': ['angularjs'],
      'node': ['nodejs', 'node.js'],
      'css': ['css3'],
      'html': ['html5'],
      'aws': ['amazon web services'],
      'gcp': ['google cloud platform', 'google cloud'],
      'k8s': ['kubernetes'],
      'docker': ['containerization'],
      'postgresql': ['postgres'],
      'mongodb': ['mongo']
    }
    
    for (const [key, alts] of Object.entries(variations)) {
      if (posSkillName === key && alts.includes(candSkillName)) return true
      if (candSkillName === key && alts.includes(posSkillName)) return true
    }
    
    return false
  }

  const calculateSkillMatch = (positionSkills: any[], positionCertifications: any[] = []): SkillMatchResult => {
    if (!positionSkills || positionSkills.length === 0) {
      return {
        percentage: 0,
        requiredMatched: 0,
        requiredMissing: 0,
        goodToHaveMatched: 0,
        totalRequired: 0,
        totalGoodToHave: 0
      }
    }

    const requiredSkills = positionSkills.filter(skill => !skill.is_good_to_have)
    const goodToHaveSkills = positionSkills.filter(skill => skill.is_good_to_have)
    
    let requiredMatched = 0
    let goodToHaveMatched = 0

    // Check required skills
    requiredSkills.forEach(posSkill => {
      const hasSkill = state.skills.some(candSkill => 
        skillsMatch(candSkill.skill_name, posSkill.skill_name)
      )
      if (hasSkill) requiredMatched++
    })

    // Check good-to-have skills
    goodToHaveSkills.forEach(posSkill => {
      const hasSkill = state.skills.some(candSkill => 
        skillsMatch(candSkill.skill_name, posSkill.skill_name)
      )
      if (hasSkill) goodToHaveMatched++
    })

    const requiredMissing = requiredSkills.length - requiredMatched
    
    // Calculate percentage: required skills are weighted 5x, good-to-have 1x
    const totalPossiblePoints = (requiredSkills.length * 5) + goodToHaveSkills.length
    const earnedPoints = (requiredMatched * 5) + goodToHaveMatched
    
    const percentage = totalPossiblePoints > 0 ? Math.round((earnedPoints / totalPossiblePoints) * 100) : 0

    return {
      percentage,
      requiredMatched,
      requiredMissing,
      goodToHaveMatched,
      totalRequired: requiredSkills.length,
      totalGoodToHave: goodToHaveSkills.length
    }
  }

  // Auto-fetch skills when user becomes authenticated as job seeker
  useEffect(() => {
    if (userState.isAuthenticated && userState.profile?.is_jobseeker && state.skills.length === 0 && !state.isLoading) {
      fetchCandidateSkills()
    }
  }, [userState.isAuthenticated, userState.profile?.is_jobseeker])

  // Clear skills when user logs out
  useEffect(() => {
    if (!userState.isAuthenticated) {
      clearSkills()
    }
  }, [userState.isAuthenticated])

  const contextValue: CandidateSkillsContextType = {
    state,
    calculateSkillMatch,
    refreshSkills,
    clearSkills
  }

  return (
    <CandidateSkillsContext.Provider value={contextValue}>
      {children}
    </CandidateSkillsContext.Provider>
  )
}

export function useCandidateSkills() {
  const context = useContext(CandidateSkillsContext)
  if (context === undefined) {
    throw new Error('useCandidateSkills must be used within a CandidateSkillsProvider')
  }
  return context
}

export function useSkillMatch() {
  const { calculateSkillMatch, state } = useCandidateSkills()
  
  return {
    calculateMatch: calculateSkillMatch,
    hasSkills: state.skills.length > 0,
    isLoading: state.isLoading,
    skillsCount: state.skills.length
  }
}